import React from "react";
import {
  Modal,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Divider,
  Tag,
  Timeline,
  Tooltip,
  Badge,
} from "antd";
import moment from "moment-timezone";
import {
  MailOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  SendOutlined,
} from "@ant-design/icons";

// Import CSS (Create this file)
import "@assets/css/ScheduledCampaignDetailModal.css";

const { Title, Text } = Typography;

const ScheduledCampaignDetailModal = ({ visible, campaign, onClose }) => {
  if (!campaign) return null;

  // Extract campaign information
  const { name, scheduledFor, emailConfiguration, timing } = campaign;

  // Format time until start
  const timeUntilStart = moment(scheduledFor).fromNow();

  // Format scheduled time
  const scheduledTime = moment(scheduledFor).format("MMM DD, YYYY HH:mm");

  // Format estimate completion
  const estimatedCompletion = timing?.estimatedCompletionTime
    ? moment(timing.estimatedCompletionTime).format("MMM DD, YYYY HH:mm")
    : "Not available";

  // Calculate estimated duration in human-readable format
  const estimatedDuration = emailConfiguration?.sendingRate?.estimatedDuration
    ? moment
        .duration(emailConfiguration.sendingRate.estimatedDuration)
        .humanize()
    : "Not available";

  // Get timezone information
  const timezone = timing?.timezone || "UTC";

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      title={
        <Space>
          <MailOutlined />
          <span>Scheduled Campaign Details - {name}</span>
        </Space>
      }
      closeIcon={<CloseOutlined style={{ color: "white" }} />}
      destroyOnClose={true}
      className="sched-campaign-modal"
    >
      {/* Schedule Information Section */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card
            className="sched-info-section"
            title={
              <Space>
                <CalendarOutlined />
                <span>Campaign Schedule</span>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div className="sched-info-box">
                  <div className="sched-info-label">
                    <ClockCircleOutlined /> Scheduled Launch
                  </div>
                  <div className="sched-info-value">
                    {scheduledTime}
                    <Badge
                      status="processing"
                      text={
                        <Text className="sched-time-remaining">
                          {timeUntilStart}
                        </Text>
                      }
                      style={{ marginLeft: 8 }}
                    />
                  </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="sched-info-box">
                  <div className="sched-info-label">
                    <EnvironmentOutlined /> Timezone
                  </div>
                  <div className="sched-info-value">{timezone}</div>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div className="sched-info-box">
                  <div className="sched-info-label">
                    <ThunderboltOutlined /> Estimated Duration
                  </div>
                  <div className="sched-info-value">{estimatedDuration}</div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="sched-info-box">
                  <div className="sched-info-label">
                    <ClockCircleOutlined /> Estimated Completion
                  </div>
                  <div className="sched-info-value">{estimatedCompletion}</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Campaign Configuration Section */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24}>
          <Card
            className="sched-info-section"
            title={
              <Space>
                <MailOutlined />
                <span>Campaign Configuration</span>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <div className="sched-stat-box">
                  <div className="sched-stat-value">
                    {emailConfiguration?.totalEmails || 0}
                  </div>
                  <div className="sched-stat-label">Total Recipients</div>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div className="sched-stat-box">
                  <div className="sched-stat-value">
                    {emailConfiguration?.sendingRate?.emailsPerHour || 0}
                  </div>
                  <div className="sched-stat-label">Emails Per Hour</div>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <div className="sched-stat-box">
                  <div className="sched-stat-value">
                    {emailConfiguration?.distributionStrategy?.totalSenders ||
                      0}
                  </div>
                  <div className="sched-stat-label">Active Senders</div>
                </div>
              </Col>
            </Row>

            <Divider />

            {/* Sending Details */}
            <div className="sched-detail-section">
              <div className="sched-detail-header">
                <InfoCircleOutlined /> Sending Details
              </div>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <div className="sched-detail-item">
                    <div className="sched-detail-label">Sending Pattern</div>
                    <div className="sched-detail-value">
                      {emailConfiguration?.sendingRate?.pattern ? (
                        <Tag color="blue">
                          {emailConfiguration.sendingRate.pattern.replace(
                            /_/g,
                            " "
                          )}
                        </Tag>
                      ) : (
                        <Tag color="default">Not specified</Tag>
                      )}
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div className="sched-detail-item">
                    <div className="sched-detail-label">
                      Minutes Between Emails
                    </div>
                    <div className="sched-detail-value">
                      {emailConfiguration?.sendingRate?.minutesBetweenEmails ||
                        "N/A"}
                    </div>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div className="sched-detail-item">
                    <div className="sched-detail-label">
                      Distribution Strategy
                    </div>
                    <div className="sched-detail-value">
                      <Tag color="purple">
                        {emailConfiguration?.distributionStrategy
                          ?.isLoadBalanced
                          ? "Load Balanced"
                          : "Standard Distribution"}
                      </Tag>
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div className="sched-detail-item">
                    <div className="sched-detail-label">Emails Per Sender</div>
                    <div className="sched-detail-value">
                      {emailConfiguration?.distributionStrategy
                        ?.emailsPerSender || "N/A"}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Sender Information Section */}
      {emailConfiguration?.senders && emailConfiguration.senders.length > 0 && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24}>
            <Card
              className="sched-info-section"
              title={
                <Space>
                  <TeamOutlined />
                  <span>Sender Information</span>
                </Space>
              }
            >
              <Timeline mode="left">
                {emailConfiguration.senders.map((sender, idx) => (
                  <Timeline.Item
                    key={idx}
                    dot={<UserOutlined style={{ fontSize: "16px" }} />}
                    color="blue"
                  >
                    <div className="sched-sender-item">
                      <div className="sched-sender-email">
                        {sender.email}
                        {sender.useAlias && sender.alias && (
                          <Tag color="purple" style={{ marginLeft: 8 }}>
                            Alias: {sender.alias}
                          </Tag>
                        )}
                        {sender.useSharedInbox && (
                          <Tag color="cyan" style={{ marginLeft: 8 }}>
                            Shared Inbox
                          </Tag>
                        )}
                      </div>
                      <div className="sched-sender-details">
                        <Space split={<Divider type="vertical" />}>
                          <span>Provider: {sender.smtpProvider || "N/A"}</span>
                          {sender.emailGroup && (
                            <span>Group: {sender.emailGroup}</span>
                          )}
                        </Space>
                      </div>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>

              {/* Distribution Summary */}
              <div className="sched-distribution-summary">
                <Space align="center">
                  <SendOutlined />
                  <Text>
                    {emailConfiguration.senders.length} senders will send
                    approximately{" "}
                    {emailConfiguration?.distributionStrategy
                      ?.emailsPerSender || "N/A"}{" "}
                    emails each
                  </Text>
                </Space>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Additional Information Section (optional) */}
      {campaign.additionalInfo && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24}>
            <Card
              className="sched-info-section"
              title={
                <Space>
                  <InfoCircleOutlined />
                  <span>Additional Information</span>
                </Space>
              }
            >
              <div className="sched-additional-info">
                {campaign.additionalInfo}
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </Modal>
  );
};

export default ScheduledCampaignDetailModal;
